import { fetchWithAuth, getApiBase } from '@/lib/api';

interface ApiResponse<T> {
  data: T;
  meta?: { version?: string };
}

export interface IUserBudgetResponse {
  usagePercent: number;
  /**
   * Spend against the user's OWN quota — the figure the analytics user table
   * shows. May exceed 100: being over your quota is not the same as being
   * blocked. `null` for a licence without a quota.
   *
   * Distinct from `usagePercent`, which is the distance to an actual block and
   * therefore also moves with what colleagues spend. In a workspace allowing
   * an 800% overshoot the two differ by a factor of eight (KAN-501).
   */
  // Optional on the wire: a client can outlive the deploy that added it, and
  // the store already falls back to null.
  personalUsagePercent?: number | null;
  status: 'ok' | 'warning' | 'exceeded';
  projectedLimitDate: string | null;
  resetDate: string;
  usingCredits: boolean;
  // Story 17.18 — workspace-level visibility
  workspaceUsagePercent: number;
  warningReason: 'personal' | 'workspace' | null;
  agentSpendPercent: number;
  // Remaining purchased credit in EUR (customer-facing, margin hidden) — admin
  // "läuft auf Guthaben (noch X übrig)". Never the internal USD budget.
  creditRemaining: number;
  // Sparmodus: paid user has exhausted personal cap or tenant pool. Chat
  // stays available but is forced onto low_budget_allowed models.
  lowBudgetMode: boolean;
}

export async function getUserBudget(): Promise<IUserBudgetResponse> {
  const res = await fetchWithAuth(`${getApiBase()}/api/v1/me/budget`);
  if (!res.ok) {
    throw new Error(`Failed to fetch user budget: ${res.status}`);
  }
  const json: ApiResponse<IUserBudgetResponse> = await res.json();
  return json.data;
}

export interface ITierRecommendation {
  recommendedTier: 'business' | 'max';
  projectedMonthlySpend: number;
  trialDaysElapsed: number;
  dailyUtilizationPercent: number;
}

export async function getTierRecommendation(): Promise<ITierRecommendation | null> {
  try {
    const res = await fetchWithAuth(`${getApiBase()}/api/v1/me/tier-recommendation`);
    if (!res.ok) return null;
    const json: ApiResponse<ITierRecommendation | null> = await res.json();
    return json.data;
  } catch {
    return null;
  }
}
